import { motion } from "framer-motion";
import { MapPin, Globe2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const ServiceDetails = () => {
  const { t } = useLang();

  return (
    <section
      id="details"
      className="relative px-6 md:px-10 py-24 md:py-32 border-t border-white/10 bg-[#0c0c0c]"
      data-testid="details-section"
    >
      <div className="max-w-[1500px] mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-[#ccff00] mb-6">
          {t.sectionLabels.details}
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-display text-[10vw] md:text-6xl lg:text-7xl whitespace-pre-line max-w-4xl"
          data-testid="details-title"
        >
          {t.details.title}
        </motion.h2>
        <p className="mt-6 text-white/60 max-w-2xl text-base md:text-lg leading-relaxed">
          {t.details.sub}
        </p>

        <div className="mt-16 space-y-4">
          {t.details.items.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
              className="grid md:grid-cols-[120px,1fr] gap-4 md:gap-10 p-7 md:p-10 rounded-3xl border border-white/10 bg-[#111111] hover:bg-[#161616] transition"
              data-testid={`detail-item-${i + 1}`}
            >
              <div className="font-display text-5xl md:text-6xl text-[#ff4500]">
                {item.n}
              </div>
              <div>
                <h3 className="font-display text-2xl md:text-3xl mb-3">{item.title}</h3>
                <p className="text-white/65 leading-relaxed text-base md:text-lg">
                  {item.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Scope of work */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 grid md:grid-cols-2 gap-5"
          data-testid="scope-block"
        >
          <div className="rounded-3xl border border-white/10 p-8 md:p-10 bg-[#ccff00] text-black">
            <div className="flex items-center gap-3 mb-4">
              <MapPin size={20} />
              <span className="text-xs uppercase tracking-[0.3em] font-semibold">
                {t.details.scope.title}
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl">
              {t.details.scope.local}
              <span className="mx-3 text-black/40">×</span>
              {t.details.scope.global}
            </h3>
            <p className="mt-5 text-black/80 leading-relaxed max-w-xl">
              {t.details.scope.desc}
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 p-8 md:p-10 bg-[#0f0f0f] flex flex-col justify-between">
            <Globe2 size={48} className="text-[#ff4500]" />
            <div className="mt-8 font-display text-3xl md:text-4xl leading-tight">
              Indonesia
              <br />
              <span className="text-white/30">×</span>{" "}
              <span className="text-[#ff4500]">Worldwide</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceDetails;
